import type {
  AlbumClaimResult,
  Artifacts,
  AvatarFrameId,
  Badges,
  Collection,
  DailyCardBack,
  DailyDraw,
  Ladder,
  LadderClaimResult,
  PickStreak,
  PlayerCard,
  PlayerLook,
  RewardBadgeRef,
  RewardBonus,
  RewardsProfile,
  RewardsStats,
  Scale,
  Season,
  SeasonEvents,
  Step,
  StreakAward,
  WeeklyCard,
  WeeklyMoment,
  WeeklyPendingBonus,
} from './models';
import { AVATAR_FRAME_IDS, nameGlow } from './models';
import type {
  AlbumClaimResultDto,
  ArtifactCountDto,
  ArtifactsDto,
  AvatarFramesDto,
  BadgesDto,
  CollectionCardDto,
  CollectionDto,
  DailyBackDto,
  DailyCardRefDto,
  DailyDto,
  DailyStreakDto,
  LadderClaimResultDto,
  LadderDto,
  PlayerCardDto,
  PlayerLookDto,
  ProfileDto,
  RewardBonusDto,
  ScaleDto,
  SeasonDto,
  SeasonEventsDto,
  StatsDto,
  StepDto,
  WeeklyCardDto,
  WeeklyDto,
  WeeklyPendingBonusDto,
} from './types';

function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * The shared payout shape. `null` stays `null` — no reward, which is not zero.
 * A body without an amount is treated the same: there is nothing to print.
 */
export function mapRewardBonus(dto: RewardBonusDto | null | undefined): RewardBonus | null {
  if (!dto || typeof dto !== 'object' || typeof dto.amt !== 'number') return null;
  return {
    type: dto.bt ?? '',
    amount: dto.amt,
    unit: dto.un ?? 'USD',
    max: typeof dto.max === 'number' ? dto.max : null,
    // Absent reads as placeable: the flag only ever holds a button back when false.
    available: dto.av !== false,
    durationDays: typeof dto.d === 'number' && dto.d > 0 ? dto.d : null,
  };
}

function frameCodes(fr: AvatarFramesDto | undefined): string[] {
  if (Array.isArray(fr)) return fr;
  if (typeof fr === 'string') return [fr];
  return fr?.i ? [fr.i] : [];
}

/** Whatever shape `fr` came in → the known frames, in ladder order, each once. */
export function asFrames(fr: AvatarFramesDto | undefined): AvatarFrameId[] {
  const codes = frameCodes(fr);
  return AVATAR_FRAME_IDS.filter((id) => codes.includes(id));
}

function badgeRef(
  dto: { id: string; img: string | null } | null | undefined,
): RewardBadgeRef | null {
  return dto ? { id: dto.id, image: dto.img ?? null } : null;
}

export function mapProfile(dto: ProfileDto): RewardsProfile {
  return {
    rankIndex: dto.r?.i ?? null,
    claimableRanks: [...(dto.rcl ?? [])].sort((a, b) => a - b),
    points: dto.p ?? 0,
    nextRankThreshold: dto.np ?? null,
    stepsClosed: dto.sc ?? 0,
    avatar: dto.av ? { id: dto.av.i, frames: asFrames(dto.av.fr) } : null,
    availableAvatarIds: dto.avs ?? [],
    glow: dto.gl ? { type: dto.gl.t, strength: dto.gl.s } : null,
    onboarding: {
      done: dto.ob?.d ?? 0,
      total: dto.ob?.t ?? 0,
      tasks: (dto.ob?.tasks ?? []).map((task) => ({
        index: task.i,
        code: task.c,
        // The backend falls back to the English column, so `t` is never empty;
        // the code is the last-resort label only for a payload that predates it.
        title: task.t && task.t.trim().length > 0 ? task.t : task.c,
        done: Boolean(task.done),
        // A build without `cl` falls back to "done and its badge not owned yet",
        // which YourQuests still checks; `cl` itself is the answer when sent.
        claimable: task.cl ?? Boolean(task.done),
        badge: badgeRef(task.bd),
        promoCode: task.pc && task.pc.trim().length > 0 ? task.pc.trim() : null,
        bonus: mapRewardBonus(task.bn),
      })),
    },
    streak: {
      current: dto.st?.cur ?? 0,
      weekDay: dto.st?.wd ?? 0,
      checkedIn: typeof dto.st?.ci === 'boolean' ? dto.st.ci : null,
      picked: typeof dto.st?.pk === 'boolean' ? dto.st.pk : null,
      nextDayAt: toDate(dto.st?.nx),
      rareDay: typeof dto.st?.rd === 'boolean' ? dto.st.rd : null,
    },
  };
}

const AWARD_KIND: Record<string, StreakAward['kind']> = {
  MEDAL: 'medals',
  CUP: 'cups',
  RELIC: 'diamonds',
};

function mapPickStreak(dto: DailyStreakDto | null | undefined): PickStreak | null {
  if (!dto) return null;
  return {
    current: dto.cur ?? 0,
    weekDay: dto.wd ?? 0,
    awards: (dto.aw ?? []).flatMap((award) => {
      const kind = AWARD_KIND[award.type];
      return kind && award.qty > 0 ? [{ kind, quantity: award.qty }] : [];
    }),
  };
}

function mapDailyBack(dto: DailyBackDto): DailyCardBack {
  return {
    slot: dto.s,
    ...(dto.k !== undefined ? { kind: dto.k } : {}),
    ...(dto.ai !== undefined ? { albumIndex: dto.ai } : {}),
    ...(dto.ci !== undefined ? { cardId: dto.ci } : {}),
    ...(dto.im !== undefined ? { image: dto.im } : {}),
  };
}

function cardRefIds(refs: DailyCardRefDto[] | undefined): string[] {
  return (refs ?? [])
    .map((ref) => (typeof ref === 'string' ? ref : (ref?.ci ?? ref?.i)))
    .filter((id): id is string => Boolean(id));
}

export function mapDaily(dto: DailyDto): DailyDraw {
  return {
    shelfComplete: Boolean(dto.sc),
    albums: (dto.als ?? []).map((album) => ({
      code: album.c,
      position: album.o ?? 0,
      collected: album.d ?? 0,
      total: album.n ?? 0,
    })),
    backs: (dto.b ?? []).map(mapDailyBack),
    hasSpecial: Boolean(dto.sp),
    pickedToday: Boolean(dto.pk),
    pickedSlot: dto.tk ?? null,
    arrived: cardRefIds(dto.xc),
    seen: cardRefIds(dto.sn),
    joker:
      dto.jk && dto.jk.left > 0
        ? { left: dto.jk.left, albumIndex: dto.jk.alb ?? null, options: dto.jk.opts ?? [] }
        : null,
    streak: mapPickStreak(dto.st),
    nextDrawAt: toDate(dto.nx),
    rareDay: Boolean(dto.rd),
  };
}

function mapWeeklyCard(dto: WeeklyCardDto): WeeklyCard {
  return {
    slot: dto.s,
    ...(dto.ci !== undefined ? { cardId: dto.ci } : {}),
    ...(dto.t !== undefined ? { title: dto.t } : {}),
    ...(dto.im !== undefined ? { image: dto.im } : {}),
    ...(dto.b !== undefined ? { bonus: mapRewardBonus(dto.b) } : {}),
  };
}

function mapPendingBonus(dto: WeeklyPendingBonusDto): WeeklyPendingBonus {
  return {
    week: dto.sw,
    cardId: dto.ci ?? null,
    title: dto.t ?? '',
    image: dto.im ?? null,
    bonus: mapRewardBonus(dto.b),
  };
}

export function mapWeekly(dto: WeeklyDto): WeeklyMoment {
  return {
    open: Boolean(dto.op),
    streak: dto.cur ?? 0,
    cardNumber: dto.sw ?? null,
    cards: (dto.o ?? []).map(mapWeeklyCard),
    pendingBonuses: (dto.pb ?? []).map(mapPendingBonus),
    outcome: dto.pr ?? null,
    pickedSlot: dto.tk ?? null,
    pickStreak: mapPickStreak(dto.st),
    promoCodeId: dto.pc ?? null,
  };
}

function artifactCount(dto: ArtifactCountDto | undefined): Artifacts['medals'] {
  return { earned: dto?.e ?? 0, spent: dto?.c ?? 0, onHand: dto?.h ?? 0 };
}

export function mapArtifacts(dto: ArtifactsDto): Artifacts {
  return {
    medals: artifactCount(dto.medals),
    cups: artifactCount(dto.cups),
    relics: artifactCount(dto.relics),
  };
}

export function mapLadder(dto: LadderDto): Ladder {
  return {
    id: dto.li,
    title: dto.t,
    description: dto.d ?? null,
    closedTiers: dto.cl ?? 0,
    metric: dto.m ?? null,
    current: dto.cur ?? 0,
    // Absent (not null) before the onboarding gate: the ladder exists, its
    // targets are hidden. `null` here means exactly that.
    tiers: dto.ts
      ? dto.ts.map((tier) => ({
          index: tier.ti,
          threshold: tier.th,
          title: tier.t,
          badge: badgeRef(tier.rw?.bd),
          hasBonus: Boolean(tier.rw?.bn),
          bonus: typeof tier.rw?.bn === 'object' ? mapRewardBonus(tier.rw.bn) : null,
          claimedAt: toDate(tier.ca),
        }))
      : null,
    claimableTier: dto.cm?.ti ?? null,
    claimableCount: dto.cm ? Math.max(1, dto.cm.n ?? 1) : 0,
    promoPendingTiers: dto.pw ?? [],
    imageUrl: dto.im ?? null,
  };
}

export function mapLadderClaim(dto: LadderClaimResultDto): LadderClaimResult {
  const badgeId = dto.bd ?? null;
  const promoCodeId = dto.pc ?? null;
  return {
    ladderId: dto.li,
    tierIndex: dto.ti,
    badgeId,
    promoCodeId,
    // A build before 01.10 claims one tier and says so only at the top level.
    tiers: dto.cl?.length
      ? dto.cl.map((tier) => ({
          tierIndex: tier.ti,
          badgeId: tier.bd ?? null,
          promoCodeId: tier.pc ?? null,
          promoPending: tier.pw ?? false,
        }))
      : [{ tierIndex: dto.ti, badgeId, promoCodeId, promoPending: false }],
  };
}

export function mapStats(dto: StatsDto): RewardsStats {
  return {
    completedTasks: dto.ct ?? 0,
    rewardedUsers: dto.ru ?? 0,
    paidAwardsUsd: typeof dto.pa === 'number' ? dto.pa : Number(dto.pa) || 0,
  };
}

function mapCollectionCard(dto: CollectionCardDto, index: number) {
  return {
    id: dto.i ?? null,
    code: null,
    title: dto.t ?? '',
    image: dto.im ?? null,
    collected: Boolean(dto.ow),
    wonAt: toDate(dto.at),
    bonus: mapRewardBonus(dto.b),
    position: dto.x ?? index,
  };
}

export function mapCollection(dto: CollectionDto): Collection {
  return {
    albums: (dto.al ?? [])
      .slice()
      .sort((a, b) => (a.o ?? 0) - (b.o ?? 0))
      .map((album) => ({
        setId: album.i,
        code: album.c,
        title: album.t,
        description: album.ds ?? null,
        image: album.im ?? null,
        position: album.o ?? 0,
        kind: album.k,
        total: album.n ?? 0,
        collected: album.d ?? 0,
        cards: (album.cd ?? [])
          .map(mapCollectionCard)
          .sort((a, b) => a.position - b.position)
          .map(({ position: _position, ...card }) => card),
        bonus: mapRewardBonus(album.bn),
        claimable: Boolean(album.cl),
        claimedAt: toDate(album.ca),
      })),
    total: dto.n ?? 0,
    collected: dto.d ?? 0,
  };
}

export function mapAlbumClaim(dto: AlbumClaimResultDto): AlbumClaimResult {
  return { setId: dto.setId, code: dto.code, promoCodeId: dto.promoCodeId ?? null };
}

export function mapStep(dto: StepDto): Step {
  const blockers = (dto.blockers ?? []).map((blocker) => ({
    ladderId: blocker.li,
    title: blocker.t,
    closedTiers: blocker.cl,
  }));
  return {
    index: dto.n,
    closed: Boolean(dto.closed),
    // `gap` is always `blockers.length`; falling back to the list keeps the two
    // from ever disagreeing on screen.
    gap: dto.gap ?? blockers.length,
    blockers,
  };
}

export function mapScale(dto: ScaleDto): Scale {
  return {
    ranks: (dto.rk ?? [])
      .map((rank) => ({
        index: rank.i,
        title: rank.t,
        threshold: rank.th ?? null,
        image: rank.im ?? null,
        bonus: mapRewardBonus(rank.bn),
      }))
      .sort((a, b) => a.index - b.index),
    avatars: (dto.av ?? [])
      .map((avatar) => ({
        id: avatar.i,
        rankIndex: avatar.r,
        position: avatar.s,
        image: avatar.im ?? null,
      }))
      .sort((a, b) => a.rankIndex - b.rankIndex || a.position - b.position),
    counts: dto.ct
      ? {
          onboardingQuests: dto.ct.ob ?? 0,
          questClasses: (dto.ct.qc ?? [])
            .filter((entry) => entry.n > 0)
            .map((entry) => ({ tier: entry.i, count: entry.n }))
            .sort((a, b) => a.tier - b.tier),
          albums: dto.ct.al ?? 0,
        }
      : null,
  };
}

export function mapPlayerLook(dto: PlayerLookDto | null | undefined): PlayerLook | null {
  if (!dto) return null;
  return {
    avatar: dto.av
      ? {
          image: dto.av.im ?? null,
          rankIndex: dto.av.r,
          position: dto.av.s,
          frames: asFrames(dto.av.fr),
        }
      : null,
    glow: nameGlow(dto.gl && { type: dto.gl.t }),
  };
}

export function mapPlayerCard(dto: PlayerCardDto): PlayerCard {
  return {
    rank: dto.r ? { index: dto.r.i, title: dto.r.t, image: dto.r.im ?? null } : null,
    avatar: dto.av
      ? {
          id: dto.av.i,
          image: dto.av.im ?? null,
          rankIndex: dto.av.r,
          position: dto.av.s,
          frames: asFrames(dto.av.fr),
        }
      : null,
    glow: dto.gl ? { type: dto.gl.t, strength: dto.gl.s } : null,
    artifacts: {
      medals: dto.art?.md ?? 0,
      cups: dto.art?.cp ?? 0,
      diamonds: dto.art?.dm ?? 0,
    },
    achievementsTotal: dto.ach ?? 0,
    featured: (dto.fe ?? []).map((badge) => ({
      id: badge.i,
      title: badge.t,
      image: badge.im ?? null,
      code: badge.c ?? null,
      group: badge.g ?? null,
      rarity: badge.r ?? null,
    })),
  };
}

export function mapBadges(dto: BadgesDto): Badges {
  return {
    items: (dto.b ?? []).map((badge) => ({
      id: badge.i,
      code: badge.c,
      group: badge.g,
      rarity: badge.r,
      title: badge.t,
      description: badge.d ?? null,
      summary: badge.sd ?? null,
      emoji: badge.e ?? null,
      image: badge.im ?? null,
      points: badge.p ?? 0,
      owned: Boolean(badge.ow),
      awardedAt: toDate(badge.at),
      featured: Boolean(badge.fe),
      live: Boolean(badge.lv),
      bonus: mapRewardBonus(badge.bn),
      progress: badge.pg ? { current: badge.pg.c, target: badge.pg.t } : null,
    })),
    claimableTasks: (dto.tc ?? []).map((task) => ({
      id: task.i,
      scopeRef: task.sr,
      badgeId: task.b,
      title: task.t,
      bonus: mapRewardBonus(task.bn),
    })),
    total: dto.n ?? 0,
    owned: dto.d ?? 0,
    points: dto.p ?? 0,
  };
}

/**
 * `GET /season`. A 204 (no season, or none of its tasks active) arrives as an
 * empty body and reads as `null`, as does a season with no tasks: the block is
 * not drawn.
 */
export function mapSeason(dto: SeasonDto | '' | null | undefined): Season | null {
  if (!dto || typeof dto !== 'object' || !Array.isArray(dto.tasks) || dto.tasks.length === 0) {
    return null;
  }
  return {
    id: dto.id,
    title: dto.t,
    daysLeft: dto.daysLeft ?? 0,
    locked: dto.lk === true,
    tasks: dto.tasks.map((task) => ({
      id: task.id,
      code: task.code,
      detector: task.dt,
      title: task.t,
      points: task.p ?? 1,
      badge: task.reward?.b
        ? {
            id: task.reward.b,
            title: task.reward.t ?? null,
            image: task.reward.im ?? null,
            rarity: task.reward.r ?? null,
          }
        : null,
      bonus: mapRewardBonus(task.reward?.bn),
      current: typeof task.cur === 'number' ? task.cur : null,
      goal: task.goal,
      status: task.st ?? null,
      description: task.d ?? null,
      summary: task.sd ?? null,
      claimedAt: toDate(task.ca),
      required: task.req
        ? task.req.map((req) => ({ code: req.code, title: req.t, done: req.done === true }))
        : null,
    })),
  };
}

export function mapSeasonEvents(dto: SeasonEventsDto): SeasonEvents {
  return {
    minEvents: dto.minEvents ?? 0,
    events: (dto.events ?? []).map((event) => ({
      id: event.eventId,
      title: event.title,
      slug: event.slug,
      image: event.image ?? null,
      traded: typeof event.traded === 'boolean' ? event.traded : null,
    })),
  };
}
